import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, OnDestroy, Output, ViewChild } from '@angular/core';
import { TireRecognitionService, TireRecognitionResponse } from 'src/app/services/tire-recognition.service';
import { LoaderService } from 'src/app/core/services/loader.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomSnackbarComponent } from '../snackbar/snackbar.component';
import { TranslateService } from '@ngx-translate/core';
import { VehiclePhotoService, PhotoConfig } from 'src/app/pages/work-orders-protocols/services/vehicle-photo.service';
import { finalize, take } from 'rxjs/operators';

export interface TireScanResult {
    dimension: string;
    fullResponse: TireRecognitionResponse;
}

@Component({
    selector: 'app-tire-scanner',
    templateUrl: './tire-scanner.component.html',
    styleUrls: ['./tire-scanner.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TireScannerComponent implements OnDestroy {
    private static readonly SNACKBAR_DURATION_MS = 4000;
    private static readonly TARGET_ID = 'tire-scan';
    private static readonly TARGET_NAME = 'Tire Scan';
    private static readonly CAPTURE_FILENAME_PREFIX = 'tire';
    private static readonly CAPTURE_DEFAULT_MIME = 'image/jpeg';
    private static readonly CAMERA_FALLBACK_ERROR = 'USE_FILE_INPUT_FALLBACK';
    private static readonly HTTP_BAD_REQUEST = 400;
    private static readonly HTTP_UNAUTHORIZED = 401;
    private static readonly HTTP_FORBIDDEN = 403;
    private static readonly VIEW_CHILD_RETRY_COUNT = 8;
    private static readonly VIEW_CHILD_RETRY_DELAY_MS = 25;

    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
    @ViewChild('cameraVideo') cameraVideo?: ElementRef<HTMLVideoElement>;
    @ViewChild('cameraCanvas') cameraCanvas?: ElementRef<HTMLCanvasElement>;

    @Output() tireDetected = new EventEmitter<TireScanResult>();
    @Output() closeModal = new EventEmitter<void>();

    isVisible = false;
    selectedFile: File | null = null;
    imagePreview: string | null = null;
    isScanning = false;
    detectedDimension: string | null = null;
    tireInfo: TireRecognitionResponse | null = null;

    cameraStream: MediaStream | null = null;
    cameraLoading: boolean = false;
    cameraError: string | null = null;
    hasMultipleCameras: boolean = false;
    currentCameraLabel: 'user' | 'environment' = 'environment';

    get canCapture(): boolean {
        return !!this.cameraStream && !this.cameraLoading && !this.isScanning && !this.imagePreview && !this.detectedDimension;
    }

    get canUpload(): boolean {
        return !this.cameraStream && !this.imagePreview && !this.detectedDimension && !this.cameraLoading && !this.isScanning;
    }

    get canScanCurrentImage(): boolean {
        return !!this.imagePreview && !this.detectedDimension && !this.isScanning;
    }

    get showRetryWithNewCapture(): boolean {
        return !!this.detectedDimension && !this.isScanning;
    }

    constructor(
        private tireRecognitionService: TireRecognitionService,
        private loaderService: LoaderService,
        private snackBar: MatSnackBar,
        private translate: TranslateService,
        private vehiclePhotoService: VehiclePhotoService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnDestroy(): void {
        this.vehiclePhotoService.stopCamera();
        this.loaderService.hide();
    }

    async open(): Promise<void> {
        this.isVisible = true;
        this.reset();

        this.startCameraLoadingState();

        try {
            const cameraElementsReady = await this.ensureCameraElementsReady();
            if (!cameraElementsReady) {
                console.error('Camera video or canvas elements not available');
                this.cameraError = this.translate.instant('protocol_orders.cameraError');
                this.cameraLoading = false;
                this.cdr.markForCheck();
                return;
            }

            const success = await this.vehiclePhotoService.startCamera(this.getPhotoConfig());
            this.applyCameraStartResult(success);
        } catch (error) {
            console.error('Error al iniciar cámara:', error);
            this.cameraError = this.translate.instant('protocol_orders.cameraError');
            this.cameraLoading = false;
        } finally {
            this.cdr.markForCheck();
        }
    }

    close(): void {
        this.isVisible = false;
        this.vehiclePhotoService.stopCamera();
        this.reset();
        this.closeModal.emit();
    }

    async capturePhoto(): Promise<void> {
        try {
            const photoResult = await this.vehiclePhotoService.capturePhoto();

            if (photoResult.success && photoResult.blob) {
                this.vehiclePhotoService.stopCamera();
                this.cameraStream = null;

                this.selectedFile = new File([photoResult.blob], `${TireScannerComponent.CAPTURE_FILENAME_PREFIX}-${Date.now()}.jpg`, {
                    type: photoResult.mimeType || TireScannerComponent.CAPTURE_DEFAULT_MIME
                });
                this.imagePreview = photoResult.base64DataUrl || photoResult.url;
                this.scanTire();
            }
        } catch (error) {
            console.error('Error capturing photo:', error);
            this.showSnackbar(this.translate.instant('tireScanner.scanError'), 'error');
        } finally {
            this.cdr.markForCheck();
        }
    }

    async switchCamera(): Promise<void> {
        this.cameraLoading = true;

        try {
            await this.vehiclePhotoService.switchCamera();
            this.syncCameraState();
        } catch (error) {
            console.error('Error switching camera:', error);
            this.cameraError = this.translate.instant('protocol_orders.cameraError');
            this.cameraLoading = false;
        } finally {
            this.cdr.markForCheck();
        }
    }

    async retryCamera(): Promise<void> {
        this.startCameraLoadingState();

        try {
            const cameraElementsReady = await this.ensureCameraElementsReady();
            if (!cameraElementsReady) {
                this.cameraError = this.translate.instant('protocol_orders.cameraError');
                this.cameraLoading = false;
                return;
            }

            const success = await this.vehiclePhotoService.startCamera(this.getPhotoConfig());
            this.applyCameraStartResult(success);
        } catch (error) {
            console.error('Error al reintentar cámara:', error);
            this.cameraError = this.translate.instant('protocol_orders.cameraError');
            this.cameraLoading = false;
        } finally {
            this.cdr.markForCheck();
        }
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] ?? null;

        if (!file) {
            return;
        }

        if (!file.type.startsWith('image/')) {
            this.showSnackbar(this.translate.instant('tireScanner.invalidImage'), 'warning');
            this.clearFileInput();
            return;
        }

        if (this.cameraStream) {
            this.vehiclePhotoService.stopCamera();
            this.cameraStream = null;
        }

        this.selectedFile = file;
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
            this.imagePreview = e.target?.result as string;
            this.cdr.markForCheck();
        };
        reader.readAsDataURL(file);
        this.scanTire();
    }

    scanTire(): void {
        if (!this.selectedFile) {
            this.showSnackbar(this.translate.instant('tireScanner.noImageSelected'), 'warning');
            return;
        }

        this.isScanning = true;
        this.detectedDimension = null;
        this.tireInfo = null;
        this.loaderService.show();

        this.tireRecognitionService.recognizeTire(this.selectedFile)
            .pipe(
                take(1),
                finalize(() => {
                    this.loaderService.hide();
                    this.isScanning = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (response: TireRecognitionResponse) => {
                    if (response.success && response.tireDimension) {
                        let dimensionText = response.tireDimension.fullDimension;

                        if (response.tireBrand) {
                            dimensionText += ` - ${response.tireBrand}`;
                            if (response.tireLine) {
                                dimensionText += ` ${response.tireLine}`;
                            }
                        }

                        this.detectedDimension = dimensionText;
                        this.tireInfo = response;

                        this.showSnackbar(
                            this.translate.instant('tireScanner.dimensionDetected', { dimension: dimensionText }),
                            'success'
                        );
                    } else {
                        this.showSnackbar(this.translate.instant('tireScanner.noDimensionFound'), 'warning');
                    }
                },
                error: (error) => {
                    console.error('Tire recognition error:', error);

                    let errorMessage = this.translate.instant('tireScanner.scanError');

                    if (error.status === TireScannerComponent.HTTP_BAD_REQUEST) {
                        errorMessage = error.message || this.translate.instant('tireScanner.invalidImage');
                    } else if (error.status === TireScannerComponent.HTTP_UNAUTHORIZED) {
                        errorMessage = this.translate.instant('tireScanner.unauthorized');
                    } else if (error.status === TireScannerComponent.HTTP_FORBIDDEN) {
                        errorMessage = this.translate.instant('tireScanner.forbidden');
                    }

                    this.showSnackbar(errorMessage, 'error');
                }
            });
    }

    private showSnackbar(message: string, type: 'success' | 'error' | 'warning'): void {
        this.snackBar.openFromComponent(CustomSnackbarComponent, {
            data: { message, type },
            duration: TireScannerComponent.SNACKBAR_DURATION_MS,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: [`snackbar-${type}`]
        });
    }

    useDimension(): void {
        if (this.detectedDimension && this.tireInfo) {
            this.tireDetected.emit({
                dimension: this.detectedDimension,
                fullResponse: this.tireInfo
            });
            this.close();
        }
    }

    reset(): void {
        this.selectedFile = null;
        this.imagePreview = null;
        this.isScanning = false;
        this.detectedDimension = null;
        this.tireInfo = null;
        this.cameraStream = null;
        this.cameraLoading = false;
        this.cameraError = null;
        this.clearFileInput();
        this.cdr.markForCheck();
    }

    private startCameraLoadingState(): void {
        this.cameraLoading = true;
        this.cameraError = null;
        this.cdr.markForCheck();
    }

    private applyCameraStartResult(success: boolean): void {
        this.syncCameraState();

        if (success) {
            return;
        }

        if (this.cameraError === TireScannerComponent.CAMERA_FALLBACK_ERROR) {
            this.cameraError = null;
            this.cameraLoading = false;
            this.cameraStream = null;
            return;
        }

        this.cameraError = this.translate.instant('protocol_orders.cameraError');
        this.cameraLoading = false;
    }

    private syncCameraState(): void {
        const cameraState = this.vehiclePhotoService.getCameraState();
        this.cameraLoading = cameraState.isLoading;
        this.cameraError = cameraState.error;
        this.cameraStream = cameraState.stream;
        this.hasMultipleCameras = cameraState.hasMultipleCameras;
        this.currentCameraLabel = cameraState.facing;
    }

    private getPhotoConfig(): PhotoConfig {
        return {
            type: 'additional',
            target: { id: TireScannerComponent.TARGET_ID, name: TireScannerComponent.TARGET_NAME }
        };
    }

    private clearFileInput(): void {
        if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.value = '';
        }
    }

    private async ensureCameraElementsReady(): Promise<boolean> {
        for (let attempt = 0; attempt < TireScannerComponent.VIEW_CHILD_RETRY_COUNT; attempt++) {
            this.cdr.detectChanges();

            if (this.cameraVideo && this.cameraCanvas) {
                this.vehiclePhotoService.setVideoElements(this.cameraVideo, this.cameraCanvas);
                return true;
            }

            await this.delay(TireScannerComponent.VIEW_CHILD_RETRY_DELAY_MS);
        }

        return false;
    }

    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
